import React, { memo, useEffect, useCallback, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import styled, { css, keyframes } from "styled-components";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatButtonProps {
  onClick: () => void;
  onColorPickerClick?: () => void;
  isChatOpen?: boolean;
  theme?: {
    accentColor?: string;
    accentBright?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  isDarkTheme?: boolean;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

const getContrastColor = (hexColor: string): string => {
  const normalized = hexColor.replace("#", "");
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized.padEnd(6, "0");

  const r = Number.parseInt(full.substring(0, 2), 16) / 255;
  const g = Number.parseInt(full.substring(2, 4), 16) / 255;
  const b = Number.parseInt(full.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.6 ? "#111111" : "#ffffff";
};

const resolvePrimarySurfaceColor = (
  accentColor: string,
  isDark: boolean,
): string => {
  if (accentColor !== "#6750A4") {
    return accentColor;
  }

  return isDark ? "#f0f0f0" : "#111111";
};

const resolvePrimaryTextColor = (
  accentColor: string,
  isDark: boolean,
  iconColor: string,
): string => {
  if (accentColor !== "#6750A4") {
    return iconColor;
  }

  return isDark ? "#111111" : "#ffffff";
};

const resolvePulseColor = (accentColor: string, isDark: boolean): string => {
  if (accentColor !== "#6750A4") {
    return accentColor;
  }

  return isDark ? "rgba(240,240,240,0.5)" : "rgba(0,0,0,0.25)";
};

const CHAT_MODAL_HEIGHT = 600;
const CHAT_MODAL_PADDING = 20;
const FAB_CLEARANCE = 28;
const COMPACT_VIEWPORT_WIDTH = 768;
const COMPACT_VIEWPORT_HEIGHT = CHAT_MODAL_HEIGHT + CHAT_MODAL_PADDING * 2 + 80;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulseRing = keyframes`
  0%   { transform: scale(1);    opacity: 0.55; }
  70%  { transform: scale(1.55); opacity: 0;    }
  100% { transform: scale(1.55); opacity: 0;    }
`;

const dotBlink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.2; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const stackVariants = {
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.12,
      delayChildren: 0.15,
      type: "spring",
      stiffness: 240,
      damping: 24,
    },
  },
  compactHidden: {
    opacity: 0,
    y: 12,
    transition: {
      duration: 0.18,
      ease: "easeOut",
      when: "afterChildren",
      staggerChildren: 0.06,
      staggerDirection: -1,
    },
  },
};

const rowVariants = {
  hidden: {
    opacity: 0,
    y: 24,
    scale: 0.88,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 280,
      damping: 22,
      mass: 0.7,
    },
  },
  exit: {
    opacity: 0,
    y: 16,
    scale: 0.94,
    transition: {
      duration: 0.18,
      ease: "easeInOut",
    },
  },
};

const actionRowVariants = {
  hidden: {
    opacity: 0,
    y: 10,
    scale: 0.92,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 320,
      damping: 24,
      mass: 0.6,
    },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.92,
    transition: {
      duration: 0.15,
      ease: "easeInOut",
    },
  },
};

const labelVariants = {
  hidden: {
    opacity: 0,
    x: 10,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.22,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    x: 10,
    scale: 0.98,
    transition: {
      duration: 0.16,
      ease: "easeInOut",
    },
  },
};

const Stack = styled(motion.div)<{
  $isChatOpen?: boolean;
  $isCompactViewport?: boolean;
}>`
  position: fixed;
  bottom: ${(p) => {
    if (!p.$isChatOpen) {
      return "32px";
    }

    if (p.$isCompactViewport) {
      return "32px";
    }

    return `calc(${CHAT_MODAL_HEIGHT}px + ${CHAT_MODAL_PADDING}px + ${FAB_CLEARANCE}px)`;
  }};
  right: 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-end;
  transition:
    bottom 0.28s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
  z-index: ${(p) => (p.$isChatOpen ? 1105 : 1000)};
  opacity: ${(p) => (p.$isChatOpen && p.$isCompactViewport ? 0 : 1)};
  pointer-events: ${(p) =>
    p.$isChatOpen && p.$isCompactViewport ? "none" : "auto"};

  @media (max-width: 768px) {
    bottom: 22px;
    right: 18px;
  }

  @media (max-width: 768px), (max-height: ${COMPACT_VIEWPORT_HEIGHT}px) {
    ${(p) =>
      p.$isChatOpen &&
      p.$isCompactViewport &&
      `
      bottom: 22px;
      right: 18px;
    `}
  }
`;

// Each row: label + button
const Row = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 60px;
`;

// Pill label driven by GSAP hover animation.
const Label = styled(motion.span)<{ $isDark: boolean }>`
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%) translateX(10px);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 5px 13px;
  border-radius: 0;
  border: 0.5px solid
    ${(p) => (p.$isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)")};
  background: ${(p) =>
    p.$isDark ? "rgba(10,10,10,0.8)" : "rgba(255,255,255,0.88)"};
  color: ${(p) => (p.$isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)")};
  backdrop-filter: blur(10px);
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
`;

// Shared button base
const buttonBase = css<{ $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  border: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: visible; /* allow pulse ring to bleed */
  flex-shrink: 0;
  /* No CSS transition on transform — GSAP owns all transform state */

  &:active {
    transform: scale(0.94) !important;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
`;

// Primary FAB — solid monochrome fill
const PrimaryButton = styled(motion.button)<{
  $size: number;
  $accentColor: string;
  $iconColor: string;
  $isDark: boolean;
}>`
  ${buttonBase}
  background: ${(p) => resolvePrimarySurfaceColor(p.$accentColor, p.$isDark)};
  color: ${(p) =>
    resolvePrimaryTextColor(p.$accentColor, p.$isDark, p.$iconColor)};

  /* Hairline inner border for depth */
  box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.15);
`;

// Pulse ring behind primary button
const PulseRing = styled.span<{ $color: string }>`
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 1.5px solid ${(p) => p.$color};
  animation: ${pulseRing} 2.4s ease-out infinite;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0;
  }
`;

// Online dot
const OnlineDot = styled.span<{ $isDark: boolean }>`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid ${(p) => (p.$isDark ? "#0a0a0a" : "#ffffff")};
  animation: ${dotBlink} 3s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Secondary FAB — ghost
const SecondaryButton = styled(motion.button)<{
  $size: number;
  $isDark: boolean;
}>`
  ${buttonBase}
  background: ${(p) =>
    p.$isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"};
  border: 0.5px solid
    ${(p) => (p.$isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)")} !important;
  color: ${(p) => (p.$isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)")};
  backdrop-filter: blur(8px);
`;

// SVG icon
const Icon = styled(motion.svg)<{ $size?: number }>`
  width: ${(p) => p.$size ?? 22}px;
  height: ${(p) => p.$size ?? 22}px;
  fill: currentColor;
  pointer-events: none;
  flex-shrink: 0;
`;

const MenuIcon = styled(Icon)`
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const ChatIcon: React.FC = () => (
  <Icon viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {/* Minimal speech-bubble path */}
    <path d="M20 2H4C2.9 2 2 2.9 2 4v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 12H5.17L4 15.17V4h16v10z" />
    {/* Subtle typing dots */}
    <circle cx="8.5" cy="9" r="1.2" />
    <circle cx="12" cy="9" r="1.2" />
    <circle cx="15.5" cy="9" r="1.2" />
  </Icon>
);

// ─── Component ────────────────────────────────────────────────────────────────

export const ChatButton: React.FC<ChatButtonProps> = memo(
  ({
    onClick,
    onColorPickerClick,
    isChatOpen = false,
    theme,
    isDarkTheme = true,
  }) => {
    const [viewportSize, setViewportSize] = useState<{
      height: number;
      width: number;
    }>(() => {
      if (globalThis.window === undefined) {
        return {
          height: Number.POSITIVE_INFINITY,
          width: Number.POSITIVE_INFINITY,
        };
      }

      return {
        height: globalThis.window.innerHeight,
        width: globalThis.window.innerWidth,
      };
    });
    const reducedMotion = useReducedMotion();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [paletteSpin, setPaletteSpin] = useState(0);

    const accentColor = theme?.accentColor ?? "#6750A4";
    const primarySurfaceColor = resolvePrimarySurfaceColor(
      accentColor,
      isDarkTheme,
    );
    const primaryIconColor = getContrastColor(primarySurfaceColor);

    useEffect(() => {
      const updateViewportSize = (): void => {
        setViewportSize({
          height: globalThis.window.innerHeight,
          width: globalThis.window.innerWidth,
        });
      };

      updateViewportSize();
      globalThis.window.addEventListener("resize", updateViewportSize);

      return () => {
        globalThis.window.removeEventListener("resize", updateViewportSize);
      };
    }, []);

    useEffect(() => {
      if (isChatOpen) {
        setIsMenuOpen(false);
      }
    }, [isChatOpen]);

    const isCompactViewport =
      viewportSize.height <= COMPACT_VIEWPORT_HEIGHT ||
      viewportSize.width <= COMPACT_VIEWPORT_WIDTH;

    const handleSecondaryClick = useCallback(() => {
      setPaletteSpin((count) => count + 1);
      onColorPickerClick?.();
      setIsMenuOpen(false);
    }, [onColorPickerClick]);

    const handleChatClick = useCallback(() => {
      onClick();
      setIsMenuOpen(false);
    }, [onClick]);

    // Derive pulse ring color for monochrome mode
    const pulseColor = resolvePulseColor(accentColor, isDarkTheme);

    const primarySize = 60;
    const secondarySize = 50;
    const shouldHideForCompactChat = isChatOpen && isCompactViewport;
    const stackState = shouldHideForCompactChat ? "compactHidden" : "visible";
    const buttonTransition = reducedMotion
      ? { duration: 0 }
      : {
          type: "spring",
          stiffness: 400,
          damping: 22,
        };

    return (
      <Stack
        initial="hidden"
        animate={stackState}
        variants={stackVariants}
        $isChatOpen={isChatOpen}
        $isCompactViewport={isCompactViewport}
      >
        <AnimatePresence initial={false}>
          {isMenuOpen && (
            <>
              {onColorPickerClick && (
                <Row
                  key="customize-row"
                  data-fab-row
                  variants={actionRowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Label
                    key="customize-label"
                    $isDark={isDarkTheme}
                    variants={labelVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    Customize me
                  </Label>
                  <SecondaryButton
                    type="button"
                    onClick={handleSecondaryClick}
                    $size={secondarySize}
                    $isDark={isDarkTheme}
                    aria-label="Customize me"
                    whileHover={
                      reducedMotion ? undefined : { scale: 1.05, y: -1 }
                    }
                    whileTap={reducedMotion ? undefined : { scale: 0.94 }}
                    transition={buttonTransition}
                  >
                    <Icon
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                      animate={{ rotate: paletteSpin * 180 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      <path d="M12 3a9 9 0 0 0 0 18h.5a1.5 1.5 0 0 0 1.5-1.5c0-.28-.05-.55-.15-.81a2 2 0 0 1 1.87-2.69H18a3 3 0 0 0 3-3 9 9 0 0 0-9-9zm-4 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3-4a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm2 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                    </Icon>
                  </SecondaryButton>
                </Row>
              )}

              <Row
                key="chat-action-row"
                data-fab-row
                variants={actionRowVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Label
                  key="chat-label"
                  $isDark={isDarkTheme}
                  variants={labelVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  Chat me
                </Label>
                <SecondaryButton
                  type="button"
                  onClick={handleChatClick}
                  $size={secondarySize}
                  $isDark={isDarkTheme}
                  aria-label="Chat me"
                  whileHover={
                    reducedMotion ? undefined : { scale: 1.05, y: -1 }
                  }
                  whileTap={reducedMotion ? undefined : { scale: 0.94 }}
                  transition={buttonTransition}
                >
                  <ChatIcon />
                </SecondaryButton>
              </Row>
            </>
          )}
        </AnimatePresence>

        <Row key="main-trigger-row" data-fab-row layout variants={rowVariants}>
          <Label
            key="menu-label"
            $isDark={isDarkTheme}
            variants={labelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {isMenuOpen ? "Close" : "Open"}
          </Label>
          <PrimaryButton
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            $size={primarySize}
            $accentColor={accentColor}
            $iconColor={primaryIconColor}
            $isDark={isDarkTheme}
            aria-label={isMenuOpen ? "Close actions" : "Open actions"}
            aria-expanded={isMenuOpen}
            whileHover={reducedMotion ? undefined : { scale: 1.06, y: -2 }}
            whileTap={reducedMotion ? undefined : { scale: 0.94 }}
            transition={buttonTransition}
          >
            <PulseRing $color={pulseColor} />
            <OnlineDot $isDark={isDarkTheme} />
            <MenuIcon
              $size={22}
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
              animate={isMenuOpen ? { rotate: 45 } : { rotate: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <path d="M12 6v12" />
              <path d="M6 12h12" />
            </MenuIcon>
          </PrimaryButton>
        </Row>
      </Stack>
    );
  },
);

export default ChatButton;
